import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import { hasAnyAuthority } from '../utils/hasAuthority';
import { Routes, Tokens } from '../constants';
import { PdfViewer } from '../components/PdfViewer';
import { ActivationExpired } from '../components/ActivationExpired';
import { ClientSelectionModal } from '../components/ClientSelectionModal';
import LoadingScreen from '../components/LoadingScreen';
import { WhatsNewPopup } from '../components/WhatsNewPopup';

interface RoleBasedGuardProps {
  children: ReactNode;
  RoutePermissions?: string[];
  RoleAccess?: string[];
  clientAccess?: boolean;
  productAccessRoutes?: string[];
}

const RoleBasedGuard = (props: RoleBasedGuardProps) => {
  const {
    children,
    RoutePermissions,
    RoleAccess,
    clientAccess,
    productAccessRoutes
  } = props;
  const {
    user,
    userRole,
    permissions,
    userClient,
    clientId,
    haveAllAccess,
    productsAccess
  } = useAuth();

  if (clientAccess && !clientId) {
    return <Navigate to={Routes.login} />;
  }

  if (RoleAccess && !hasAnyAuthority([userRole], RoleAccess)) {
    return <Navigate to={Routes.login} />;
  }

  if (RoutePermissions && !hasAnyAuthority(permissions, RoutePermissions) && !haveAllAccess) {
    return <Navigate to={Routes.login} />;
  }

  if (productAccessRoutes && !haveAllAccess && !hasAnyAuthority(productsAccess, productAccessRoutes)) {
    return <Navigate to={Routes.home} />;
  }

  if (!localStorage.getItem(Tokens.accessToken)) {
    return <Navigate to={Routes.login} />;
  }

  const modalSequence = () => {
    if (haveAllAccess && user) {
      return (<></>);
    }
    if (!clientId) {
      return <ClientSelectionModal isModal={!clientId} />;
    }
    const clientExpiry = (userClient && !userClient?.active);
    if (clientExpiry) {
      return <ActivationExpired isModal={clientExpiry} />;
    }
    const clientEula = (!userClient?.UserClient?.eula?.current?.accepted && userClient?.UserClient?.eula?.current?.fileId);
    if (clientEula) {
      return <PdfViewer isModal={clientEula} />;
    }
    return <WhatsNewPopup />;
  };

  return (
    <>
      {modalSequence()}
      {
        ((clientId || haveAllAccess) && user) ? (
          children
        ) : (
          <LoadingScreen />
        )
      }
    </>
  );
};

export default RoleBasedGuard;
